import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ModalArticleInfo from '../components/molecules/ModalArticleInfo/ModalArticleInfoBrandAware';
import { ReactComponent as MensureLogo } from '../assets/logo.svg';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Grid,
  Divider,
  Dialog,
  DialogContent
} from '@mui/material';
import {
  Share as ShareIcon,
  OpenInNew as OpenInNewIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  Language as WebIcon,
  Article as ArticleIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  TouchApp as ActionsIcon,
  Visibility as VisibilityIcon,
  ThumbUp as ThumbUpIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Comment as CommentIcon,
  Group as GroupIcon,
  Public as PublicIcon,
  TrendingUp as TierIcon,
  SentimentSatisfiedAlt as PositiveIcon,
  SentimentNeutral as NeutralIcon,
  SentimentDissatisfied as NegativeIcon,
  AutoAwesome as AIIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
  RestartAlt as RestartAltIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Newspaper as NewspaperIcon,
  MenuBook as MenuBookIcon,
  Person as PersonIcon,
  Speed as ImpactIcon,
  PeopleAlt as AudienceIcon,
  Category as CategoryIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Map as MapIcon,
  Star as DedicatedIcon,
  Groups as SharedIcon,
  FormatQuote as MentionIcon,
  Remove as NoneExposureIcon
} from '@mui/icons-material';
import api from '../services/api';
import ModernToast from '../components/molecules/ModernToast/ModernToast';
import { useTheme } from '../design-system/providers/ThemeProvider';
import { colors } from '../design-system/tokens';
import { getVideoDimensions, getImageDimensions } from '../utils/mediaDetector';
import CleanContentRenderer from '../components/atoms/CleanContentRenderer/CleanContentRenderer';
import TextHighlighter from '../components/atoms/TextHighlighter';
import TextToSpeechPlayer from '../components/atoms/TextToSpeechPlayer';
import AppTooltip from '../components/atoms/Tooltip/Tooltip';
import KpiaResultsModal from '../components/organisms/Dialogs/KpiaResultsModal/KpiaResultsModal';
import { SidebarQualitativeCTA } from '../components/organisms/Dialogs/ArticleModal/EditorialIntelligencePanel';
import ArticleShareMenu from '../components/molecules/ArticleShareMenu';

// Atomic Design Components
import TwitterEmbed from '../components/organisms/TwitterEmbed';
import InstagramEmbed from '../components/organisms/InstagramEmbed';
import InstagramCarousel from '../components/organisms/Dialogs/ArticleModal/InstagramCarousel';
import ThreadsEmbed from '../components/organisms/ThreadsEmbed';
import YouTubeEmbed from '../components/organisms/YouTubeEmbed';
import LinkedInEmbed from '../components/organisms/LinkedInEmbed';
import PlatformIcon from '../components/atoms/PlatformIcon';

// Utils
import { getYouTubeId, formatNumber, decodeHtmlEntities } from '../utils/contentProcessor';
import { getEffectiveTier } from '../utils/effectiveTier';

const MENSURE_FALLBACK_IMAGE = '/mensure-thumbnail.png';

const handleArticleImageFallback = (event) => {
  if (event.currentTarget.src.includes(MENSURE_FALLBACK_IMAGE)) return;
  event.currentTarget.src = MENSURE_FALLBACK_IMAGE;
  event.currentTarget.style.objectFit = 'contain';
};

const getRadioWaveformSeed = (article) => {
  const source = `${article?.title || ''}${article?.siteName || ''}${article?._id || ''}`;
  return source.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
};

const PublicRadioWaveform = ({ article }) => {
  const audioSource = article.audioUrl || article.videoUrl;
  const seed = getRadioWaveformSeed(article);
  const barCount = 96;
  const bars = Array.from({ length: barCount }, (_, index) => {
    const wave = Math.sin((index + seed) * 0.58) + Math.sin((index + seed) * 0.19);
    const normalized = (wave + 2) / 4;
    const accentLift = index % 9 === 0 ? 10 : 0;
    return Math.round(16 + normalized * 62 + accentLift);
  });

  return (
    <Box
      id="audio-playback-section"
      sx={{
        mb: 3,
        width: '100%',
        borderRadius: '8px',
        overflow: 'hidden',
        position: 'relative',
        minHeight: { xs: 260, sm: 380, md: 520 },
        background: 'linear-gradient(135deg, #15191c 0%, #1e2b2f 54%, #102724 100%)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 18% 18%, rgba(34, 197, 94, 0.12), transparent 34%), radial-gradient(circle at 84% 72%, rgba(14, 165, 233, 0.1), transparent 36%)',
          pointerEvents: 'none'
        }
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: { xs: '62px 28px 92px', sm: '86px 48px 108px' },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: '3px', sm: '4px' },
          opacity: 0.96
        }}
      >
        {bars.map((height, index) => (
          <Box
            key={`${height}-${index}`}
            sx={{
              flex: '1 1 0',
              minWidth: 2,
              maxWidth: 8,
              height: `${height}%`,
              borderRadius: '999px',
              backgroundColor: index < Math.round(barCount * 0.44)
                ? 'rgba(45, 212, 191, 0.96)'
                : 'rgba(226, 232, 240, 0.46)',
              boxShadow: index < Math.round(barCount * 0.44)
                ? '0 0 14px rgba(45, 212, 191, 0.24)'
                : 'none',
              transformOrigin: 'center',
              animation: `publicRadioWavePulse 2.8s ease-in-out ${index * 0.025}s infinite`
            }}
          />
        ))}
      </Box>

      {audioSource && (
        <Box
          sx={{
            position: 'absolute',
            left: { xs: 18, sm: 28 },
            right: { xs: 18, sm: 28 },
            bottom: { xs: 18, sm: 24 },
            zIndex: 1,
            '& audio': {
              width: '100%',
              display: 'block'
            }
          }}
        >
          <audio controls preload="metadata" src={audioSource}>
            Seu navegador nao suporta audio.
          </audio>
        </Box>
      )}

      <Box
        component="style"
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes publicRadioWavePulse {
              0%, 100% { transform: scaleY(0.92); opacity: 0.88; }
              50% { transform: scaleY(1); opacity: 1; }
            }
          `
        }}
      />
    </Box>
  );
};

/**
 * Converte conteúdo do artigo (JSON blocks ou HTML) para texto puro para TTS
 */
const convertContentToPlainText = (content) => {
  if (!content) return '';

  // Se for array de blocos JSON (novo formato)
  if (Array.isArray(content)) {
    return content
      .map(block => {
        // Blocos podem ser strings diretas
        if (typeof block === 'string') {
          return block;
        }

        // Ou objetos com campo 'content'
        if (typeof block === 'object' && block.content) {
          return block.content;
        }

        return '';
      })
      .filter(text => text && text.trim().length > 0)
      .join('. ');
  }

  // Se for string (HTML ou texto)
  if (typeof content === 'string') {
    // Remover tags HTML
    let text = content.replace(/<[^>]*>/g, ' ');

    // Substituir entidades HTML
    text = text
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    // Remover múltiplos espaços e quebras de linha
    text = text.replace(/\s+/g, ' ').trim();

    return text;
  }

  return '';
};

// Helper para obter configuração de exposição
const getExposureConfig = (exposure) => {
  switch (exposure) {
    case 'dedicated': return { icon: DedicatedIcon, label: 'Dedicada', description: 'Matéria focada na marca', color: '#4caf50' };
    case 'shared': return { icon: SharedIcon, label: 'Compartilhada', description: 'Marca junto com concorrentes', color: '#ff9800' };
    case 'mention': return { icon: MentionIcon, label: 'Citação', description: 'Apenas uma menção breve', color: '#2196f3' };
    case 'none': return { icon: NoneExposureIcon, label: 'Sem exposição', description: 'Marca não mencionada', color: '#9e9e9e' };
    default: return null;
  }
};

// Helper para obter configuração de sentimento
const getSentimentConfig = (sentiment) => {
  switch (sentiment) {
    case 'positive': return { icon: PositiveIcon, label: 'Positivo', color: '#4caf50' };
    case 'negative': return { icon: NegativeIcon, label: 'Negativo', color: '#f44336' };
    default: return { icon: NeutralIcon, label: 'Neutro', color: '#9e9e9e' };
  }
};

const getPublicExposureTooltipConfig = (exposure) => {
  switch (exposure) {
    case 'dedicated': return { icon: DedicatedIcon, label: 'Dedicada', description: 'Matéria focada na classificação do artigo', color: '#4caf50' };
    case 'shared': return { icon: SharedIcon, label: 'Compartilhada', description: 'Classificação aparece junto com concorrentes', color: '#ff9800' };
    case 'mention': return { icon: MentionIcon, label: 'Citação', description: 'Apenas uma menção breve da classificação', color: '#2196f3' };
    case 'none': return { icon: NoneExposureIcon, label: 'Sem exposição', description: 'Classificação não mencionada', color: '#9e9e9e' };
    default: return null;
  }
};

const PublicArticlePage = () => {
  const { articleId } = useParams();
  const [searchParams] = useSearchParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareMenuAnchor, setShareMenuAnchor] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Estados para zoom em tela cheia (Jornal/Revista)
  // Verificar se deve abrir fullscreen automaticamente via URL param
  const shouldOpenFullscreen = searchParams.get('fullscreen') === 'true';
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenZoom, setFullscreenZoom] = useState(100);
  const [imageZoom, setImageZoom] = useState(100);
  const [faviconError, setFaviconError] = useState(false);
  const [kpiaOpen, setKpiaOpen] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const videoRef = React.useRef(null);
  const contentContainerRef = React.useRef(null);
  const briefingKeywords = useMemo(
    () => article?.publicBriefingKeywords || { primary: [], competitor: [] },
    [article?.publicBriefingKeywords]
  );
  const { currentTheme, primaryGradient, setTheme, setFontFamily, setFontSize } = useTheme();

  // Forcçar tema escuro, Montserrat na página pública
  React.useEffect(() => {
    setTheme('escuro');
    setFontFamily('Montserrat, sans-serif');
    // setFontSize('0.75rem'); // REMOVIDO: bloqueava o tamanho customizado do título
  }, [setTheme, setFontFamily, setFontSize]);

  // Escutar evento do botão "Ver Análise Qualitativa" (disparado pelo ModalArticleInfo)
  React.useEffect(() => {
    const handler = () => setKpiaOpen(true);
    window.addEventListener('openKpiaModal', handler);
    return () => window.removeEventListener('openKpiaModal', handler);
  }, []);

  // Auto-abrir fullscreen se parâmetro ?fullscreen=true está presente
  React.useEffect(() => {
    if (shouldOpenFullscreen && article && !loading) {
      setFullscreenZoom(50); // Zoom inicial de 50%
      setFullscreenOpen(true);
    }
  }, [shouldOpenFullscreen, article, loading]);

  // Helper para formatar categoria
  const formatCategory = (category) => {
    if (!category) return null;
    const categoryMap = {
      'portal': 'Portal',
      'blog': 'Blog',
      'site': 'Site',
      'jornal': 'Jornal',
      'revista': 'Revista',
      'rede-social': 'Rede Social',
      'governo': 'Governo',
      'outro': 'Outro'
    };
    return categoryMap[category] || category;
  };

  // Helper para formatar exposição
  const formatExposureLabel = (exposure) => {
    if (!exposure) return '-';
    const exposureMap = {
      'dedicated': 'Dedicada',
      'shared': 'Compartilhada',
      'mention': 'Citação',
      'none': 'Nenhuma'
    };
    return exposureMap[exposure] || exposure;
  };

  // Helper para obter configuração de sentimento (usando design system)
  const getSentimentConfig = (sentiment) => {
    switch (sentiment) {
      case 'positive':
        return {
          icon: PositiveIcon,
          label: 'Positivo',
          color: colors.sentiment.positive.main,
          bgColor: colors.sentiment.positive.bg
        };
      case 'negative':
        return {
          icon: NegativeIcon,
          label: 'Negativo',
          color: colors.sentiment.negative.main,
          bgColor: colors.sentiment.negative.bg
        };
      default:
        return {
          icon: NeutralIcon,
          label: 'Neutro',
          color: colors.sentiment.neutral.main,
          bgColor: colors.sentiment.neutral.bg
        };
    }
  };

  // Detecta vídeo do YouTube
  const youtubeId = useMemo(() => {
    if (!article) return null;
    return article.youtubeId || getYouTubeId(article.videoUrl || article.siteUrl);
  }, [article]);


  // Limita o título para visual mais agradável (máximo 100 caracteres)
  const truncatedTitle = useMemo(() => {
    if (!article?.title) return '';
    return article.title.length > 100
      ? article.title.substring(0, 100) + '...'
      : article.title;
  }, [article?.title]);

  // Helper para obter embedCode (fallback para embedHtml em artigos antigos)
  const embedCode = useMemo(() => {
    if (!article) return null;
    return article.embedCode || article.embedHtml || null;
  }, [article]);

  const tagNames = useMemo(() => (
    (article?.tags || [])
      .map(tag => (typeof tag === 'object' ? tag?.name : null))
      .filter(Boolean)
  ), [article?.tags]);

  const classificationName = tagNames[0] || 'Classificação principal';
  const classificationEvidence = tagNames.length > 0
    ? `Classificação do artigo: ${tagNames.join(', ')}`
    : `Classificação do artigo: ${classificationName}`;
  const semanticHighlights = useMemo(() => {
    const occupancy = article?.editorialOccupancy || article?.onlineCentimetragem || {};
    const brandEntity = Array.isArray(occupancy.entityBreakdown)
      ? occupancy.entityBreakdown.find(entity => entity?.type === 'brand')
      : null;
    const spokespersons = [
      occupancy.spokespersonFound,
      brandEntity?.spokespersonFound,
      article?.spokespersonFound
    ].filter(Boolean);
    const keyMessages = [
      ...(Array.isArray(occupancy.keyMessagesFound) ? occupancy.keyMessagesFound : []),
      ...(Array.isArray(brandEntity?.keyMessagesFound) ? brandEntity.keyMessagesFound : []),
      ...(Array.isArray(article?.keyMessagesFound) ? article.keyMessagesFound : [])
    ].filter(Boolean);

    return [
      ...Array.from(new Set(spokespersons)).map(text => ({
        type: 'spokesperson',
        text,
        label: 'Porta-voz'
      })),
      ...Array.from(new Set(keyMessages)).map(text => ({
        type: 'keyMessage',
        text,
        label: 'Mensagem-chave'
      }))
    ];
  }, [article]);

  const PublicTooltipContent = ({ eyebrow, title, description, insight, evidence }) => (
    <Box sx={{ maxWidth: 320, color: 'rgba(17, 24, 39, 0.92)' }}>
      {eyebrow && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 0.5,
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: 'primary.main'
          }}
        >
          {eyebrow}
        </Typography>
      )}
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, lineHeight: 1.35, color: 'rgba(17, 24, 39, 0.96)' }}>
        {title}
      </Typography>
      {description && (
        <Typography sx={{ mt: 0.75, fontSize: '0.76rem', lineHeight: 1.55, color: 'rgba(55, 65, 81, 0.88)' }}>
          {description}
        </Typography>
      )}
      {evidence && (
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'rgba(15, 23, 42, 0.12)' }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'rgba(17, 24, 39, 0.96)', mb: 0.35 }}>
            Base da classificação
          </Typography>
          <Typography sx={{ fontSize: '0.74rem', lineHeight: 1.55, color: 'rgba(55, 65, 81, 0.88)' }}>
            {evidence}
          </Typography>
        </Box>
      )}
      {insight && (
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'rgba(15, 23, 42, 0.12)' }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'rgba(17, 24, 39, 0.96)', mb: 0.35 }}>
            Insight da IA
          </Typography>
          <Typography sx={{ fontSize: '0.74rem', lineHeight: 1.55, color: 'rgba(55, 65, 81, 0.88)' }}>
            {insight}
          </Typography>
        </Box>
      )}
    </Box>
  );



  useEffect(() => {
    if (articleId) {
      fetchArticle();
    }
  }, [articleId]);


  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/articles/public/${articleId}`);
      // Debug: verificar se cropArea está sendo retornado
      console.log('📐 [DEBUG] Article loaded:', {
        id: response.data.data?._id,
        mediaType: response.data.data?.mediaType,
        domainInfo: response.data.data?.domainInfo,
        audience: response.data.data?.audience,
        audienceMonthly: response.data.data?.audienceMonthly,
        valuation: response.data.data?.valuation
      });
      setArticle(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar artigo:', error);
      setError('Artigo não encontrado ou não está disponível publicamente');
    } finally {
      setLoading(false);
    }
  };

  // Helper para verificar se é mídia impressa (Jornal/Revista)
  const isPrintMedia = useMemo(() => {
    return ['Jornal', 'Revista'].includes(article?.mediaType);
  }, [article?.mediaType]);

  const printPages = useMemo(() => {
    if (!article) return [];

    const byImage = new Map();
    const addPage = (page, fallbackNumber) => {
      const image = typeof page === 'string' ? page : page?.image || page?.url;
      if (!image || byImage.has(image)) return;
      byImage.set(image, {
        ...(typeof page === 'string' ? {} : page),
        pageNumber: (typeof page === 'string' ? fallbackNumber : page?.pageNumber) || byImage.size + 1,
        image,
        thumbnail: typeof page === 'string' ? null : page?.thumbnail
      });
    };

    if (Array.isArray(article.pages)) {
      article.pages.forEach((page, index) => addPage(page, index + 1));
    }

    addPage({
      pageNumber: 1,
      image: article.image,
      thumbnail: article.thumbnail,
      cropArea: article.cropArea,
      markers: article.markers
    }, 1);

    if (Array.isArray(article.allImages)) {
      article.allImages.forEach((image, index) => addPage(image, index + 1));
    }

    if (Array.isArray(article.carouselMedia)) {
      article.carouselMedia
        .filter(media => media?.type === 'image')
        .forEach((media, index) => addPage(media, index + 1));
    }

    return Array.from(byImage.values()).map((page, index) => ({
      ...page,
      pageNumber: page.pageNumber || index + 1
    }));
  }, [article]);

  const hasMultiplePrintPages = printPages.length > 1;
  const currentPrintPage = printPages[currentPageIndex] || printPages[0];
  const currentPrintImage = currentPrintPage?.image || article?.image;

  useEffect(() => {
    setCurrentPageIndex(0);
  }, [article?._id]);

  const printArticleAreas = useMemo(() => {
    if (!article) return [];

    const source = currentPrintPage || article;
    const ocrMarkers = Array.isArray(source.markers)
      ? source.markers.filter(marker => marker?.type === 'ocr' && marker?.coordinates)
      : [];

    const areas = ocrMarkers.length > 0
      ? ocrMarkers.map(marker => marker.coordinates)
      : (Array.isArray(source.cropArea) ? source.cropArea : (source.cropArea ? [source.cropArea] : []));

    return areas.filter(area => (
      area
      && area.x !== undefined
      && area.y !== undefined
      && area.width !== undefined
      && area.height !== undefined
    ));
  }, [article, currentPrintPage]);
  const renderPrintArticleAreas = (areas = []) => areas.map((area, index) => (
    <Box
      key={`article-area-${index}`}
      sx={{
        position: 'absolute',
        left: `${area.x}%`,
        top: `${area.y}%`,
        width: `${area.width}%`,
        height: `${area.height}%`,
        border: '2px dashed #1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.12)',
        boxSizing: 'border-box',
        pointerEvents: 'none',
        zIndex: 2,
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.08)'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -28,
          left: -2,
          px: 1,
          py: 0.25,
          bgcolor: '#1976d2',
          color: '#fff',
          fontSize: '0.75rem',
          fontWeight: 700,
          borderRadius: '4px 4px 0 0',
          whiteSpace: 'nowrap'
        }}
      >
        {areas.length > 1 ? `Matéria ${index + 1}` : 'Matéria'}
      </Box>
    </Box>
  ));

  // Funções de zoom para mídia impressa
  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setImageZoom(prev => Math.max(prev - 25, 50));
  };

  const handleFullscreen = () => {
    setFullscreenZoom(50); // Abre em 50% para boa leitura inicial
    setFullscreenOpen(true);
  };

  const handleFullscreenZoomIn = () => {
    setFullscreenZoom(prev => Math.min(prev + 25, 300));
  };

  const handleFullscreenZoomOut = () => {
    setFullscreenZoom(prev => Math.max(prev - 25, 25));
  };

  const handleFullscreenZoomReset = () => {
    setFullscreenZoom(100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Data não informada';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      // Usar horário LOCAL do browser (sem forçar UTC)
      // A data no banco é salva como meia-noite no horário de Brasília
      // Ex: 2026-03-30T00:00Z para UTC-3 = 2026-03-29T21:00Z no UTC
      // getUTCDate() retornaria 29 (errado) — toLocaleDateString retorna 30 (correto)
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Funções de compartilhamento
  const handleShareMenuOpen = (event) => {
    setShareMenuAnchor(event.currentTarget);
  };

  const handleShareMenuClose = () => {
    setShareMenuAnchor(null);
  };

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // Função legada para compatibilidade
  const handleShare = (event) => {
    handleShareMenuOpen(event);
  };

  const publicSidebarPanelSx = (active, offset) => ({
    position: { xs: active ? 'relative' : 'absolute', md: 'absolute' },
    inset: 0,
    height: { xs: 'auto', md: '100%' },
    minHeight: 0,
    overflowY: { xs: 'visible', md: 'auto' },
    overflowX: 'hidden',
    opacity: active ? 1 : 0,
    transform: {
      xs: active ? 'translateY(0)' : `translateY(${offset})`,
      md: active ? 'translateX(0)' : `translateX(${offset})`
    },
    transition: 'opacity 220ms cubic-bezier(0.22, 1, 0.36, 1), transform 260ms cubic-bezier(0.22, 1, 0.36, 1)',
    pointerEvents: active ? 'auto' : 'none',
    visibility: active ? 'visible' : 'hidden'
  });

  const PublicArticleSidebar = () => (
    <Box
      sx={{
        position: { xs: 'relative', md: 'sticky' },
        top: { md: 0 },
        height: { xs: 'auto', md: '85vh' },
        maxHeight: { md: '85vh' },
        minHeight: { md: 520 },
        overflow: 'hidden',
        bgcolor: '#1b1d21',
        borderRadius: 3,
        p: 2
      }}
    >
      <Box sx={{ position: 'relative', height: { xs: 'auto', md: '100%' }, minHeight: 0 }}>
        <Box sx={publicSidebarPanelSx(!kpiaOpen, '-18px')}>
          <SidebarQualitativeCTA article={article} onClick={() => setKpiaOpen(true)} />
          <ModalArticleInfo article={article} enableBriefingKeywords={false} hideQualitativeButton={true} sx={{ my: 0, p: 0, border: 0, bgcolor: 'transparent' }} />
        </Box>

        <Box sx={publicSidebarPanelSx(kpiaOpen, '18px')}>
          <KpiaResultsModal
            embedded
            embeddedCompact
            embeddedBackLabel="Voltar às métricas"
            onClose={() => setKpiaOpen(false)}
            article={article}
            enableBriefingKeywords={false}
          />
        </Box>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error || !article) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ textAlign: 'center', maxWidth: 520, px: 3 }}>
          <Box
            component={MensureLogo}
            aria-hidden
            sx={{
              width: { xs: 180, sm: 228, md: 260 },
              height: 'auto',
              mb: 4,
              color: 'text.primary',
              opacity: 0.9
            }}
          />
          <Typography variant="h4" fontWeight={800} color="text.primary" gutterBottom>
            Artigo não encontrado
          </Typography>
          <Typography variant="body1" color="text.secondary">
            O artigo que você está procurando não existe ou não está disponível publicamente.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#282a2f' }}>
      {/* Novo Header Minimalista (Estilo Modal) */}
      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 }, pb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' }, 
          justifyContent: 'space-between', 
          gap: 3,
          mb: 4 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, minWidth: 0 }}>
            {/* Ícone da Plataforma (Nítido e Monocromático) */}
            <PlatformIcon 
              mediaType={article.mediaType} 
              size={42} 
              color="text.primary" 
              sx={{ display: { xs: 'none', md: 'block' } }} 
            />
            
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h1"
                component="a"
                href={article.siteUrl || article.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: { xs: '0.9rem', md: '1.3rem' },
                  fontWeight: 700,
                  lineHeight: { xs: 1.15, md: 1.25 },
                  mb: { xs: 0.25, md: 1 },
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: 'text.primary',
                  textDecoration: 'none',
                  pointerEvents: 'auto',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {decodeHtmlEntities(article.title)}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {/* Favicon (Identidade da Fonte) */}
                {(() => {
                  const faviconSrc = article.favicon ||
                    (article.siteUrl ? (() => { try { const u = new URL(article.siteUrl); return `${u.origin}/favicon.ico`; } catch { return null; } })() : null);

                  if (faviconSrc && !faviconError) {
                    return (
                      <img
                        src={faviconSrc}
                        alt=""
                        style={{ 
                          width: 20, 
                          height: 20, 
                          borderRadius: '4px',
                          display: 'block'
                        }}
                        onError={() => setFaviconError(true)}
                      />
                    );
                  }
                  return null;
                })()}
                
                <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: { xs: '0.7rem', md: '0.85rem' }, lineHeight: 1.2 }}>
                  {article.siteName}
                  {article.section && (
                    <Box component="span" sx={{ mx: 1, opacity: 0.5 }}>•</Box>
                  )}
                  {article.section}
                  <Box component="span" sx={{ mx: 1, opacity: 0.5 }}>•</Box>
                  {formatDate(article.date || article.publishedAt || article.createdAt)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Botões de Ação Rápidos - Escondidos no Mobile pois agora ficam na Bottom Bar */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            gap: 1, 
            alignSelf: 'center'
          }}>
            <Tooltip title="Compartilhar" arrow>
              <IconButton
                onClick={handleShare}
                variant="contained"
                sx={{ 
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  width: 40,
                  height: 40,
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                <ShareIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {/* ════════════════════════════════════════════════════════
            METRICS STRIP — Apenas Mobile (linha de texto com · )
            EXATAMENTE IGUAL AO MODAL INTERNO
            ════════════════════════════════════════════════════════ */}
        {(() => {
          const isSocialMedia = ['Twitter','Instagram','Facebook','YouTube','TikTok','LinkedIn','Pinterest','Threads','Kwai'].includes(article.mediaType);
          const isOnline = article.mediaType === 'Online';
          const effectiveTier = getEffectiveTier(article);

          // Monta tokens na ordem: Tier · Sentimento · Audiência · Valoração · Exposição
          const tokens = [];

          // 1. Tier
          if (effectiveTier) {
            const tierColors = { 1: '#4caf50', 2: '#ff9800', 3: '#9e9e9e' };
            tokens.push({
              label: `Tier ${effectiveTier}`,
              color: tierColors[effectiveTier],
            });
          }

          // 2. Sentimento
          if (article.sentiment) {
            const sentColors = { positive: '#4caf50', negative: '#f44336', neutral: '#9e9e9e' };
            const sentLabels = { positive: 'Positivo', negative: 'Negativo', neutral: 'Neutro' };
            tokens.push({
              label: sentLabels[article.sentiment] || article.sentiment,
              color: sentColors[article.sentiment],
            });
          }

          // 3. Audiência — com prefixo contextual
          const audience = isSocialMedia
            ? (article.subscribersNumber || 0)
            : isOnline ? (article.audience || article.subscribersNumber || 0) : 0;
          if (audience > 0) {
            const audPrefix = isSocialMedia ? 'Seg.' : 'Aud.';
            tokens.push({ label: `${audPrefix} ${formatNumber(audience)}` });
          }

          // 4. Valoração
          if (article.valuation > 0) {
            tokens.push({
              label: `R$ ${article.valuation.toLocaleString('pt-BR', { notation: 'compact', maximumFractionDigits: 1 })}`,
            });
          }

          // 5. Exposição (Opcional, mas útil na página pública)
          if (article.exposure) {
            const exposureLabels = { dedicated: 'Dedicada', shared: 'Compartilhada', mention: 'Citação', none: 'Nenhuma' };
            tokens.push({
               label: exposureLabels[article.exposure] || article.exposure
            });
          }

          if (tokens.length === 0) return null;

          return (
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                alignItems: 'center',
                flexWrap: 'nowrap',
                overflowX: 'auto',
                gap: 0,
                px: 1,
                py: 1,
                mb: 1.5,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                flexShrink: 0,
              }}
            >
              {tokens.map((token, idx) => (
                <React.Fragment key={idx}>
                  {idx > 0 && (
                    <Typography
                      component="span"
                      sx={{ fontSize: '0.7rem', color: 'text.disabled', mx: 0.75, flexShrink: 0 }}
                    >
                      ·
                    </Typography>
                  )}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 500,
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      color: token.color || 'text.secondary',
                    }}
                  >
                    {token.label}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          );
        })()}
        <Divider />
      </Container>

      <TextHighlighter
        containerRef={contentContainerRef}
        highlights={[]}
        articleId={article?._id}
        isEnabled={false}
        briefingKeywords={briefingKeywords}
        showBriefingHighlights
        semanticHighlights={semanticHighlights}
        contentDependency={article?._id}
      />

      {/* LAYOUT ESPECIAL PARA JORNAL/REVISTA */}
      {isPrintMedia ? (
        <Container maxWidth="lg" sx={{ pb: 4 }}>
          <Grid container spacing={4}>
            {/* Conteúdo Principal */}
            <Grid item xs={12} md={8}>
              <Paper ref={contentContainerRef} elevation={1} sx={{ p: 4, border: 1, borderColor: 'rgba(255,255,255,0.08)', bgcolor: '#282a2f' }}>
                {/* Frame da Imagem com proporção correta */}
                <Box sx={{ mb: 4 }}>
                  {/* Header com controles */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {article.mediaType === 'Revista' ? <MenuBookIcon color="primary" /> : <NewspaperIcon color="primary" />}
                      <Typography variant="subtitle1" fontWeight={600} color="primary">
                        {article.mediaType === 'Revista' ? 'Página da Revista' : 'Página do Jornal'}
                      </Typography>
                    </Box>
                    <Tooltip title="Ver página original com zoom">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FullscreenIcon />}
                        onClick={handleFullscreen}
                      >
                        Página Original
                      </Button>
                    </Tooltip>
                  </Box>

                  {/* Frame da página - mostra a página completa com a área da matéria marcada */}
                  <Box
                    sx={{
                      width: '100%',
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                  >
                    {currentPrintImage ? (
                      <>
                        {/* Para mídia impressa com áreas selecionadas: mostrar apenas os recortes */}
                        {false ? (() => {
                          // Priorizar markers (Novo Sistema). Fallback para cropArea (Legado).
                          const ocrMarkers = article.markers?.filter(m => m.type === 'ocr');
                          const hasMarkers = ocrMarkers && ocrMarkers.length > 0;

                          const cropAreas = hasMarkers
                            ? ocrMarkers.map(m => m.coordinates)
                            : (Array.isArray(article.cropArea) ? article.cropArea : [article.cropArea]);

                          // Função para gerar URL recortada do Cloudinary
                          const getCroppedUrl = (imageUrl, crop) => {
                            if (!imageUrl || !imageUrl.includes('cloudinary.com') || !crop) {
                              return imageUrl;
                            }

                            const { x, y, width, height } = crop;

                            // Validar percentuais
                            if (x === undefined || y === undefined || width === undefined || height === undefined) {
                              return imageUrl;
                            }

                            // Margem assimétrica: 0.2% na esquerda (shift para direita), 0 na topo
                            // 0.5% na largura (extra direita) e 0.7% no fundo
                            const relX = Math.min(1, (x / 100) + 0.002).toFixed(5);
                            const relY = (y / 100).toFixed(5);
                            const relW = Math.min(1, (width / 100) + 0.005).toFixed(5);
                            const relH = Math.min(1, (height / 100) + 0.007).toFixed(5);

                            // Inserir transformação de crop na URL
                            const transformation = `fl_relative,c_crop,w_${relW},h_${relH},x_${relX},y_${relY}`;

                            return imageUrl.replace(
                              '/image/upload/',
                              `/image/upload/${transformation}/`
                            );
                          };

                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                              {cropAreas.map((crop, index) => {
                                if (!crop) return null;

                                const croppedUrl = getCroppedUrl(currentPrintImage, crop);

                                return (
                                  <Box key={index} sx={{ textAlign: 'center' }}>
                                    {cropAreas.length > 1 && (
                                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        Parte {index + 1} de {cropAreas.length}
                                      </Typography>
                                    )}
                                    <img
                                      src={croppedUrl}
                                      onError={handleArticleImageFallback}
                                      alt={`${article.title || 'Publicação'} - Parte ${index + 1}`}
                                      style={{
                                        maxWidth: '100%',
                                        height: 'auto',
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                      }}
                                    />
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        })() : (
                          /* Para outros tipos ou sem cropArea: mostrar imagem completa */
                          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                            <img
                              src={currentPrintImage}
                              onError={handleArticleImageFallback}
                              alt={`Página de ${article.siteName || article.mediaType}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                display: 'block'
                              }}
                            />
                            {renderPrintArticleAreas(printArticleAreas)}
                            {hasMultiplePrintPages && (
                              <>
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 12,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    display: 'flex',
                                    gap: 0.65,
                                    zIndex: 3,
                                    px: 1,
                                    py: 0.75,
                                    borderRadius: 999,
                                    bgcolor: 'rgba(0,0,0,0.22)',
                                    backdropFilter: 'blur(4px)'
                                  }}
                                >
                                  {printPages.map((_, index) => (
                                    <Box
                                      key={index}
                                      onClick={() => setCurrentPageIndex(index)}
                                      sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        bgcolor: index === currentPageIndex ? 'rgba(255,255,255,0.96)' : 'rgba(255,255,255,0.48)',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
                                        cursor: 'pointer'
                                      }}
                                    />
                                  ))}
                                </Box>

                                {currentPageIndex > 0 && (
                                  <IconButton
                                    aria-label="Pagina anterior"
                                    onClick={() => setCurrentPageIndex(prev => Math.max(0, prev - 1))}
                                    sx={{
                                      position: 'absolute',
                                      left: 12,
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      zIndex: 3,
                                      width: 38,
                                      height: 38,
                                      bgcolor: 'rgba(255,255,255,0.86)',
                                      color: '#262626',
                                      boxShadow: '0 2px 10px rgba(0,0,0,0.22)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.96)' }
                                    }}
                                  >
                                    <PrevIcon sx={{ color: '#111827', fontSize: 28 }} />
                                  </IconButton>
                                )}

                                {currentPageIndex < printPages.length - 1 && (
                                  <IconButton
                                    aria-label="Proxima pagina"
                                    onClick={() => setCurrentPageIndex(prev => Math.min(printPages.length - 1, prev + 1))}
                                    sx={{
                                      position: 'absolute',
                                      right: 12,
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      zIndex: 3,
                                      width: 38,
                                      height: 38,
                                      bgcolor: 'rgba(255,255,255,0.86)',
                                      color: '#262626',
                                      boxShadow: '0 2px 10px rgba(0,0,0,0.22)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.96)' }
                                    }}
                                  >
                                    <NextIcon sx={{ color: '#111827', fontSize: 28 }} />
                                  </IconButton>
                                )}
                              </>
                            )}
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                        <ArticleIcon sx={{ fontSize: 64, opacity: 0.5, mb: 2 }} />
                        <Typography>Imagem não disponível</Typography>
                      </Box>
                    )}
                  </Box>
                  {hasMultiplePrintPages && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                      <Chip size="small" label={`Pagina ${currentPageIndex + 1} de ${printPages.length}`} />
                    </Box>
                  )}
                </Box>

                {/* Resumo abaixo da imagem */}
                {article.description && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Resumo
                    </Typography>
                    <Typography variant="body1" paragraph sx={{
                      bgcolor: 'action.hover',
                      p: 2,
                      borderRadius: 1,
                      fontStyle: 'italic',
                      borderLeft: 4,
                      borderColor: 'primary.main'
                    }}>
                      {article.description}
                    </Typography>
                  </Box>
                )}

                {/* Ouvir Artigo - Text to Speech */}
                {(article.content || article.description) && (
                  <Box sx={{ mb: 4, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                    <TextToSpeechPlayer
                      text={(() => {
                        const parts = [];
                        if (article.title) parts.push(article.title);
                        if (article.description) parts.push(`Resumo: ${article.description}`);
                        const plainTextContent = convertContentToPlainText(article.content);
                        if (plainTextContent && plainTextContent !== article.description) {
                          parts.push(`Conteúdo: ${plainTextContent}`);
                        }
                        return parts.join('. ');
                      })()}
                      title={article.title}
                    />
                  </Box>
                )}

                {/* Conteúdo Extraído abaixo do resumo */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Conteúdo da Matéria
                  </Typography>

                  {article.content ? (
                    <CleanContentRenderer
                      contentBlocks={Array.isArray(article.content) ? article.content : null}
                      htmlContent={!Array.isArray(article.content) ? article.content : null}
                      allImages={[]}
                      mediaType={article.mediaType}
                      contentType={article.contentType}
                      videoStorageMethod={article.videoStorageMethod}
                      sx={{
                        lineHeight: 1.8,
                        '& p': { mb: 2 },
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          mt: 3,
                          mb: 2,
                          color: 'primary.main'
                        }
                      }}
                    />
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      Conteúdo não disponível
                    </Typography>
                  )}
                </Box>

                {/* Tags */}
                {article.tags && article.tags.length > 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom color="primary">
                      Tags
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {article.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag.name || tag}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} md={4}>
              <PublicArticleSidebar />
            </Grid>
          </Grid>
        </Container >
      ) : (
        /* LAYOUT PADRÃO PARA OUTRAS MÍDIAS */
        <Container maxWidth="lg" sx={{ pb: 4 }}>
          <Grid container spacing={4}>
            {/* Conteúdo Principal */}
            <Grid item xs={12} md={8}>
              <Paper ref={contentContainerRef} elevation={1} sx={{ p: 4, border: 1, borderColor: 'rgba(255,255,255,0.08)', bgcolor: '#282a2f' }}>
                {/* LINKEDIN: Render path simplificado */}
                {article.mediaType === 'LinkedIn' ? (
                  <>
                    <LinkedInEmbed article={article} isOpen={true} sx={{ mb: 3 }} />

                    {/* Descrição/Conteúdo do post */}
                    {article.description && (
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                          Conteúdo
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {article.description}
                        </Typography>
                      </Box>
                    )}

                    {/* Ouvir Artigo - Text to Speech */}
                    {(article.content || article.description) && (
                      <Box sx={{ mb: 4, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                        <TextToSpeechPlayer
                          text={(() => {
                            const parts = [];
                            if (article.title) parts.push(article.title);
                            if (article.description) parts.push(`Resumo: ${article.description}`);
                            const plainTextContent = convertContentToPlainText(article.content);
                            if (plainTextContent && plainTextContent !== article.description) {
                              parts.push(`Conteúdo: ${plainTextContent}`);
                            }
                            return parts.join('. ');
                          })()}
                          title={article.title}
                        />
                      </Box>
                    )}

                    {/* Tags */}
                    {article.tags && article.tags.length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                          Tags
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {article.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag.name || tag}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    {/* YouTube Embed - Atomic Design Organism */}
                    <YouTubeEmbed article={article} sx={{ mb: 3 }} />

                    {/* Twitter Embed - Atomic Design Organism */}
                    <TwitterEmbed article={article} isOpen={true} sx={{ mb: 3 }} />

                    {/* Instagram Carousel - Shows carousel navigation for posts with multiple images */}
                    {article.mediaType === 'Instagram' && article.embedCode && (
                      <InstagramCarousel article={article} />
                    )}

                    {/* Threads Embed - Atomic Design Organism */}
                    <ThreadsEmbed article={article} isOpen={true} sx={{ mb: 3 }} />

                    {/* Facebook Embed - Renderiza embed do Facebook Reel/Video */}
                    {/* Só mostrar embed se NÃO for do Cloudinary */}
                    {article.mediaType === 'Facebook' && embedCode && article.videoStorageMethod !== 'cloudinary' && (
                      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                        <Box
                          dangerouslySetInnerHTML={{ __html: embedCode }}
                          sx={{ '& iframe': { maxWidth: '100%', borderRadius: '8px' } }}
                        />
                      </Box>
                    )}

                    {/* TikTok Embed - Usa CleanContentRenderer para carregar o script */}
                    {article.mediaType === 'TikTok' && embedCode && (
                      <Box
                        sx={{
                          my: 2,
                          display: 'flex',
                          justifyContent: 'center',
                          width: '100%',
                          maxWidth: '100%',
                          mx: 'auto'
                        }}
                      >
                        <CleanContentRenderer
                          contentBlocks={null}
                          htmlContent={null}
                          allImages={[]}
                          mediaType={article.mediaType}
                          contentType={article.contentType}
                          embedHtml={embedCode}
                          videoStorageMethod={article.videoStorageMethod}
                          sx={{
                            width: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </Box>
                    )}

                    {/* Video News Embed - Para vídeos de portais (Globo, R7, etc) */}
                    {embedCode && (embedCode.includes('videos.globo.com') || embedCode.includes('record.r7.com')) && (
                      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                        <Box
                          dangerouslySetInnerHTML={{ __html: embedCode }}
                          sx={{
                            width: '100%',
                            maxWidth: '720px',
                            '& iframe': {
                              width: '100%',
                              aspectRatio: '16/9',
                              borderRadius: '8px',
                              border: 'none'
                            }
                          }}
                        />
                      </Box>
                    )}

                    {/* Spotify Embed - Para podcasts e músicas */}
                    {embedCode && embedCode.includes('spotify.com') && (
                      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                        <Box
                          dangerouslySetInnerHTML={{ __html: embedCode }}
                          sx={{
                            width: '100%',
                            maxWidth: '720px',
                            '& iframe': {
                              width: '100%',
                              minHeight: embedCode.includes('/video') ? '352px' : '152px',
                              borderRadius: '12px',
                              border: 'none'
                            }
                          }}
                        />
                      </Box>
                    )}

                    {/* Vídeo hospedado diretamente (não YouTube, não Twitter, não TikTok/Facebook/LinkedIn) */}
                    {(() => {
                      // LinkedIn NUNCA mostra vídeo hospedado (sempre usa embed)
                      if (article.mediaType === 'LinkedIn') return null;

                      if (article.mediaType === 'Radio' || article.mediaType === 'Rádio') {
                        return <PublicRadioWaveform article={article} />;
                      }

                      // Stories (Instagram/Facebook) sempre mostram vídeo se disponível
                      const isStory = article.contentType === 'story';

                      // PRIORIDADE: videoStorageMethod=cloudinary > videoUrl > embed
                      // REGRA: Se videoStorageMethod=cloudinary, SEMPRE mostrar vídeo (ignora embed)
                      const isCloudinaryVideo = article.videoStorageMethod === 'cloudinary';
                      const shouldShowVideo = article.videoUrl && !youtubeId &&
                        article.mediaType !== 'Twitter' &&
                        article.mediaType !== 'YouTube' &&
                        !(article.mediaType === 'Instagram' && embedCode) && // EXCLUSÃO GLOBAL: Evita duplicar com o embed
                        (
                          isStory ||                          // Stories sempre mostram vídeo
                          isCloudinaryVideo ||                // Se vídeo está no Cloudinary, SEMPRE mostra
                          (                                   // Senão, só mostra videoUrl se não tiver embed
                            article.videoUrl &&
                            !(article.mediaType === 'TikTok' && embedCode) &&
                            !(article.mediaType === 'Facebook' && embedCode)
                          )
                        );

                      if (!shouldShowVideo) return null;

                      // Aplicar dimensões dinâmicas baseadas no tipo de conteúdo
                      const videoDimensions = getVideoDimensions(article);

                      // Pinterest usa estilo especial com bordas mais arredondadas
                      const isPinterest = article.mediaType === 'Pinterest';
                      const videoRadius = isPinterest ? 24 : 8;

                      return (
                        <Box
                          sx={{
                            mb: 3,
                            textAlign: 'center',
                            ...(isPinterest && {
                              display: 'flex',
                              justifyContent: 'center'
                            })
                          }}
                          onMouseEnter={() => {
                            if (videoRef.current) {
                              const playPromise = videoRef.current.play();
                              if (playPromise !== undefined) {
                                playPromise.catch(error => {
                                  console.log('Playback prevented:', error);
                                });
                              }
                            }
                          }}
                          onMouseLeave={() => {
                            if (videoRef.current) {
                              videoRef.current.pause();
                              videoRef.current.currentTime = 0;
                            }
                          }}
                        >
                          <Box
                            sx={{
                              borderRadius: `${videoRadius}px`,
                              overflow: 'hidden',
                              display: 'inline-block',
                              ...(isPinterest && {
                                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                                width: '360px',
                                maxWidth: '100%'
                              })
                            }}
                          >
                            <video
                              ref={videoRef}
                              src={article.videoUrl}
                              poster={article.thumbnail || article.image}
                              muted
                              loop
                              controls
                              style={{
                                borderRadius: videoRadius,
                                width: isPinterest ? '100%' : videoDimensions.width,
                                maxWidth: isPinterest ? '360px' : videoDimensions.maxWidth,
                                height: isPinterest ? 'auto' : videoDimensions.height,
                                maxHeight: isPinterest ? '640px' : undefined,
                                margin: videoDimensions.margin,
                                objectFit: isPinterest ? 'cover' : 'contain',
                                display: 'block'
                              }}
                            />
                          </Box>
                        </Box>
                      );
                    })()}

                    {/* Imagem principal logo após o título */}
                    {/* LinkedIn NUNCA mostra imagem (sempre usa embed) */}
                    {/* Globo NUNCA mostra imagem (sempre usa embed de vídeo) */}
                    {/* Stories com vídeo NUNCA mostram imagem (vídeo tem prioridade) */}
                    {/* Mostra imagem quando:
                  1. Não tem YouTube ID E não tem videoUrl local (vídeos hospedados), OU
                  2. É Instagram sem embedCode (só tem imagem), OU
                  3. Tem imagem mas o vídeo é do Twitter (vídeo será mostrado no embed)
              */}
                    {article.mediaType !== 'LinkedIn' &&
                      article.mediaType !== 'Radio' &&
                      article.mediaType !== 'Rádio' &&
                      // Stories com vídeo usam o player, não mostram imagem
                      !(article.contentType === 'story' && article.videoUrl) &&
                      // Upload manual de vídeo: thumb só vai pro card, não aparece na página pública
                      !(article.videoStorageMethod === 'cloudinary') &&
                      article.image && !youtubeId &&
                      (
                        // Caso 1: Não tem vídeo nenhum
                        !article.videoUrl ||
                        // Caso 2: É Instagram com vídeo mas sem embed (mostrar imagem como fallback)
                        (article.mediaType === 'Instagram' && !embedCode)
                      ) &&
                      // Não mostrar se for Instagram COM embed (o embed já mostra a imagem)
                      !(article.mediaType === 'Instagram' && embedCode) &&
                      // Não mostrar se for Twitter (já tem embed)
                      !(article.mediaType === 'Twitter') &&
                      // Não mostrar se for TikTok COM embed (o embed já mostra o vídeo)
                      !(article.mediaType === 'TikTok' && embedCode) &&
                      // Não mostrar se for Threads COM embed (o embed já mostra o conteúdo)
                      !(article.mediaType === 'Threads' && embedCode) &&
                      // Não mostrar se for Facebook COM embed (o embed já mostra o vídeo)
                      !(article.mediaType === 'Facebook' && embedCode) &&
                      // Não mostrar se for portal de vídeo/áudio COM embed (Globo, R7, Spotify, etc - o embed já mostra o conteúdo)
                      !(embedCode && (embedCode.includes('videos.globo.com') || embedCode.includes('record.r7.com') || embedCode.includes('spotify.com'))) &&
                      (() => {
                        const imageDimensions = getImageDimensions(article);
                        const isPrintMedia = ['Jornal', 'Revista'].includes(article.mediaType);

                        return (
                          <Box sx={{
                            mb: 3,
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}>
                            {isPrintMedia ? (
                              // Jornal/Revista: imagem clicável que abre página de zoom
                              <Box
                                onClick={() => window.open(`/print/${article._id}`, '_blank')}
                                sx={{
                                  position: 'relative',
                                  cursor: 'pointer',
                                  '&:hover .zoom-overlay': {
                                    opacity: 1
                                  },
                                  '&:hover img': {
                                    transform: 'scale(1.02)',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                                  }
                                }}
                              >
                                <img
                                  src={currentPrintImage}
                                  onError={handleArticleImageFallback}
                                  alt={article.title}
                                  style={{
                                    maxWidth: imageDimensions.maxWidth,
                                    width: imageDimensions.width,
                                    height: imageDimensions.height,
                                    display: 'block',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    objectFit: imageDimensions.objectFit,
                                    boxSizing: 'border-box',
                                    margin: imageDimensions.margin,
                                    transition: 'all 0.3s ease'
                                  }}
                                />
                                {/* Overlay com ícone de zoom */}
                                <Box
                                  className="zoom-overlay"
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'rgba(0,0,0,0.4)',
                                    borderRadius: '8px',
                                    opacity: 0,
                                    transition: 'opacity 0.3s ease'
                                  }}
                                >
                                  <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    color: 'white'
                                  }}>
                                    <ZoomInIcon sx={{ fontSize: 48, mb: 1 }} />
                                    <Typography variant="body1" fontWeight={600}>
                                      Clique para ampliar
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            ) : (
                              // Outras mídias: imagem normal
                              <img
                                src={article.image}
                                onError={handleArticleImageFallback}
                                alt={article.title}
                                style={{
                                  maxWidth: imageDimensions.maxWidth,
                                  width: imageDimensions.width,
                                  height: imageDimensions.height,
                                  display: 'block',
                                  borderRadius: '8px',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                  objectFit: imageDimensions.objectFit,
                                  boxSizing: 'border-box',
                                  margin: imageDimensions.margin
                                }}
                              />
                            )}
                          </Box>
                        );
                      })()}

                    {article.description && (
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                          Resumo
                        </Typography>
                        <Typography
                          variant="body1"
                          paragraph
                          sx={{
                            bgcolor: 'action.hover',
                            p: 2,
                            borderRadius: 1,
                            fontStyle: 'italic',
                            borderLeft: 4,
                            borderColor: 'primary.main'
                          }}
                        >
                          {article.description}
                        </Typography>
                      </Box>
                    )}

                    {/* Ouvir Artigo - Text to Speech */}
                    {(article.content || article.description) && (
                      <Box sx={{ mb: 4, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                        <TextToSpeechPlayer
                          text={(() => {
                            const parts = [];
                            if (article.title) parts.push(article.title);
                            if (article.description) parts.push(`Resumo: ${article.description}`);
                            const plainTextContent = convertContentToPlainText(article.content);
                            if (plainTextContent && plainTextContent !== article.description) {
                              parts.push(`Conteúdo: ${plainTextContent}`);
                            }
                            return parts.join('. ');
                          })()}
                          title={article.title}
                        />
                      </Box>
                    )}

                    {/* Conteúdo - NÃO mostrar para LinkedIn/TikTok/Facebook com embed (já tem embed dedicado acima) */}
                    {!((article.mediaType === 'LinkedIn' || article.mediaType === 'TikTok' || article.mediaType === 'Facebook') && embedCode) && (
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                          Conteúdo
                        </Typography>

                        {/* Renderizar conteúdo - SUPORTA NOVO FORMATO JSON E LEGACY HTML */}
                        {article.content || (article.allImages && article.allImages.length > 0) || embedCode ? (
                          (() => {
                            // Twitter, Instagram e Threads com embed mostram imagens adicionais
                            const isSocialMedia = ['Twitter', 'Instagram', 'Threads'].includes(article.mediaType);

                            if (isSocialMedia) {
                              // Se tem embed (Twitter ou Instagram com embedCode), pular a primeira imagem
                              // pois já está sendo mostrada no embed
                              const hasEmbed = article.mediaType === 'Twitter' ||
                                (article.mediaType === 'Instagram' && embedCode);

                              const imagesToShow = hasEmbed
                                ? (article.allImages?.slice(1) || []) // Pula a primeira (já no embed)
                                : (article.allImages?.filter(img => {
                                  // Normalizar para comparação usando ID
                                  const getImageId = (url) => {
                                    try {
                                      const urlObj = new URL(url);
                                      if (urlObj.pathname.includes('/api/proxy/instagram-image')) {
                                        const originalUrl = urlObj.searchParams.get('url');
                                        if (originalUrl) return getImageId(originalUrl);
                                      }
                                      const parts = urlObj.pathname.split('/');
                                      const filename = parts[parts.length - 1];
                                      const idMatch = filename.match(/(\d+_)+n\.(jpg|png|webp)/);
                                      return idMatch ? idMatch[0] : urlObj.pathname.toLowerCase();
                                    } catch {
                                      return url.toLowerCase();
                                    }
                                  };
                                  return getImageId(img) !== getImageId(article.image);
                                }) || []);

                              if (imagesToShow.length === 0) {
                                return (
                                  <Typography variant="body1" color="text.secondary">
                                    Conteúdo não disponível
                                  </Typography>
                                );
                              }

                              const embedForRenderer = embedCode;

                              return (
                                <CleanContentRenderer
                                  contentBlocks={null}
                                  htmlContent={null}
                                  allImages={imagesToShow}
                                  mediaType={article.mediaType}
                                  contentType={article.contentType}
                                  embedHtml={embedForRenderer}
                                  videoStorageMethod={article.videoStorageMethod}
                                  sx={{
                                    lineHeight: 1.8,
                                    '& p': { mb: 2 },
                                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                                      mt: 3,
                                      mb: 2,
                                      color: 'primary.main'
                                    }
                                  }}
                                />
                              );
                            }

                            // Para conteúdo normal
                            return (
                              <CleanContentRenderer
                                contentBlocks={Array.isArray(article.content) ? article.content : null}
                                htmlContent={!Array.isArray(article.content) ? article.content : null}
                                allImages={article.allImages?.filter(img => img !== article.image) || []}
                                mediaType={article.mediaType}
                                contentType={article.contentType}
                                embedHtml={article.mediaType === 'Facebook' ? null : embedCode}
                                videoStorageMethod={article.videoStorageMethod}
                                sx={{
                                  lineHeight: 1.8,
                                  '& p': { mb: 2 },
                                  '& h1, & h2, & h3, & h4, & h5, & h6': {
                                    mt: 3,
                                    mb: 2,
                                    color: 'primary.main'
                                  }
                                }}
                              />
                            );
                          })()
                        ) : (
                          <Typography variant="body1" color="text.secondary">
                            Conteúdo não disponível
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Tags */}
                    {article.tags && article.tags.length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                          Tags
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {article.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag.name || tag}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Paper>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} md={4}>
              <PublicArticleSidebar />
            </Grid>
          </Grid>
        </Container>
      )}

      {/* Dialog de Tela Cheia para Zoom (Jornal/Revista) */}
      <Dialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        maxWidth={false}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.95)',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogContent sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        }}>
          {/* Barra de controles */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}>
            {/* Informações da matéria */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ color: 'white', mb: 0.5 }}>
                  {article?.siteName || (article?.mediaType === 'Revista' ? 'Revista' : 'Jornal')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {article?.date && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ScheduleIcon sx={{ fontSize: 14 }} />
                      {formatDate(article.date)}
                    </Typography>
                  )}
                  {article?.author && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 14 }} />
                      {article.author}
                    </Typography>
                  )}
                  {article?.valuation > 0 && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MoneyIcon sx={{ fontSize: 14 }} />
                      R$ {article.valuation.toLocaleString('pt-BR')}
                    </Typography>
                  )}

                  {hasMultiplePrintPages && (
                    <Chip
                      size="small"
                      label={`Pagina ${currentPageIndex + 1} de ${printPages.length}`}
                      sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'white' }}
                    />
                  )}                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Diminuir zoom">
                <IconButton
                  onClick={handleFullscreenZoomOut}
                  disabled={fullscreenZoom <= 25}
                  sx={{ color: 'white' }}
                >
                  <ZoomOutIcon />
                </IconButton>
              </Tooltip>

              <Chip
                label={`${fullscreenZoom}%`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  minWidth: 70
                }}
              />

              <Tooltip title="Aumentar zoom">
                <IconButton
                  onClick={handleFullscreenZoomIn}
                  disabled={fullscreenZoom >= 300}
                  sx={{ color: 'white' }}
                >
                  <ZoomInIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Resetar zoom">
                <IconButton onClick={handleFullscreenZoomReset} sx={{ color: 'white' }}>
                  <RestartAltIcon />
                </IconButton>
              </Tooltip>

              <Box sx={{ width: 16 }} />

              <Tooltip title="Fechar">
                <IconButton onClick={() => setFullscreenOpen(false)} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Área da imagem com scroll */}
          <Box sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            p: 4,
            cursor: fullscreenZoom > 100 ? 'grab' : 'default',
            '&:active': {
              cursor: fullscreenZoom > 100 ? 'grabbing' : 'default'
            }
          }}>
            {hasMultiplePrintPages && (
              <>
                {currentPageIndex > 0 && (
                  <IconButton
                    aria-label="Pagina anterior"
                    onClick={() => setCurrentPageIndex(prev => Math.max(0, prev - 1))}
                    sx={{
                      position: 'fixed',
                      left: 24,
                      top: '50%',
                      zIndex: 3,
                      bgcolor: 'rgba(255,255,255,0.94)',
                      color: '#1f2937',
                      border: '1px solid rgba(17,24,39,0.12)',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
                      '&:hover': { bgcolor: '#ffffff' },
                      '& .MuiSvgIcon-root': { fontSize: 26, color: '#1f2937' }
                    }}
                  >
                    <PrevIcon sx={{ color: '#111827', fontSize: 28 }} />
                  </IconButton>
                )}
                {currentPageIndex < printPages.length - 1 && (
                  <IconButton
                    aria-label="Proxima pagina"
                    onClick={() => setCurrentPageIndex(prev => Math.min(printPages.length - 1, prev + 1))}
                    sx={{
                      position: 'fixed',
                      right: 24,
                      top: '50%',
                      zIndex: 3,
                      bgcolor: 'rgba(255,255,255,0.94)',
                      color: '#1f2937',
                      border: '1px solid rgba(17,24,39,0.12)',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
                      '&:hover': { bgcolor: '#ffffff' },
                      '& .MuiSvgIcon-root': { fontSize: 26, color: '#1f2937' }
                    }}
                  >
                    <NextIcon sx={{ color: '#111827', fontSize: 28 }} />
                  </IconButton>
                )}
              </>
            )}
            {currentPrintImage && (
              <Box
                sx={{
                  position: 'relative',
                  width: `${fullscreenZoom}%`,
                  maxWidth: 'none',
                  transition: 'width 0.3s ease'
                }}
              >
                <img
                  src={currentPrintImage}
                  onError={handleArticleImageFallback}
                  alt={`Página de ${article.siteName || article.mediaType}`}
                  style={{
                    width: '100%',
                    height: 'auto',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    borderRadius: 4,
                    display: 'block'
                  }}
                />
                {isPrintMedia && renderPrintArticleAreas(printArticleAreas)}
              </Box>
            )}
          </Box>

          {/* Footer com informações */}
          <Box sx={{
            p: 1,
            textAlign: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.8)',
            borderTop: '1px solid rgba(255,255,255,0.1)'
          }}>
            {hasMultiplePrintPages ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, py: 0.5 }}>
                {printPages.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentPageIndex(index)}
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: index === currentPageIndex ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.38)',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                Use os controles acima para ajustar o zoom
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════
          BOTTOM ACTION BAR — Apenas Mobile (barra fixa no rodapé)
          ═══════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'sticky',
          bottom: 0,
          zIndex: 1000,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          px: 1,
          pt: 1.5,
          pb: 'calc(1.1rem + env(safe-area-inset-bottom, 0px))',
          alignItems: 'center',
          gap: 1,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.1)'
        }}
      >
        {[
          {
            id: 'original',
            icon: <OpenInNewIcon sx={{ fontSize: 22 }} />,
            label: 'Original',
            onClick: () => window.open(article.siteUrl || article.videoUrl, '_blank'),
            color: 'primary.main'
          },
          {
            id: 'listen',
            icon: <VisibilityIcon sx={{ fontSize: 22 }} />,
            label: 'Ouvir',
            onClick: () => {
                // Scroll até o player de áudio
                const player = document.querySelector('.tts-player-container');
                if (player) {
                    player.scrollIntoView({ behavior: 'smooth' });
                } else {
                    // Fallback visual
                    const audioSection = document.getElementById('audio-playback-section');
                    if (audioSection) audioSection.scrollIntoView({ behavior: 'smooth' });
                }
            },
            color: 'primary.main'
          },
          {
            id: 'share',
            icon: <ShareIcon sx={{ fontSize: 22 }} />,
            label: 'Partilhar',
            onClick: handleShare,
            color: 'primary.main'
          }
        ].map((btn) => (
          <Box
            key={btn.id}
            onClick={btn.onClick}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
              color: 'text.secondary',
              '&:active': { opacity: 0.6, color: btn.color },
            }}
          >
            <Box sx={{ 
                bgcolor: 'action.hover', 
                p: 1.2, 
                borderRadius: '12px', 
                display: 'flex',
                color: 'text.primary'
            }}>
                {btn.icon}
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                fontWeight: 600,
                lineHeight: 1,
                color: 'inherit',
              }}
            >
              {btn.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <ArticleShareMenu
        anchorEl={shareMenuAnchor}
        article={article}
        originalUrl={article?.siteUrl || article?.url || article?.videoUrl}
        publicUrl={typeof window !== 'undefined' ? window.location.href : ''}
        onClose={handleShareMenuClose}
        onNotify={showSnackbar}
      />

      {/* Toast de Confirmação */}
      <ModernToast
        open={snackbarOpen}
        message={snackbarMessage}
        severity="success"
        onClose={() => setSnackbarOpen(false)}
      />

    </Box >
  );
};

export default PublicArticlePage;










